import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  ArrowLeft, Send, Eye, Upload, FileText, DollarSign, Signature,
  Building2, Mail, Phone, MapPin, Image, File, Trash2, Plus,
  ChevronDown, ChevronUp, CheckCircle, Edit2, Settings, CreditCard, AlertTriangle,
  Loader2
} from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// View mode labels
const VIEW_MODES = {
  lump_sum: "Lump Sum",
  bulleted: "Bulleted",
  item_names: "Item Names",
  item_totals: "Item Totals", 
  full_breakdown: "Full Breakdown"
};

const ProposalBuilder = () => {
  const { proposalId, estimateId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [company, setCompany] = useState(null);
  const [termsTemplates, setTermsTemplates] = useState([]);
  
  // Dialogs
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  
  // Send form
  const [sendForm, setSendForm] = useState({
    send_email: true,
    send_sms: false,
    email_subject: "Your Proposal is Ready",
    email_message: "",
    sms_message: "",
    recipient_email: "",
    recipient_phone: ""
  });
  
  // Attachment form
  const [newAttachment, setNewAttachment] = useState({
    file_url: "",
    file_type: "document",
    filename: "",
    label: ""
  });
  const [uploadingFile, setUploadingFile] = useState(false);
  const [attachmentMode, setAttachmentMode] = useState("upload"); // "upload" or "link"

  const fetchProposal = useCallback(async () => {
    try {
      if (proposalId) {
        // Editing existing proposal
        const res = await API.get(`/proposals/${proposalId}`);
        setProposal(res.data);
        setEstimate(res.data.estimate);
        setCompany(res.data.company);
        setSendForm(prev => ({
          ...prev,
          recipient_email: res.data.client_email || "",
          recipient_phone: res.data.client_phone || ""
        }));
      } else if (estimateId) {
        // Creating new proposal from estimate
        const res = await API.post("/proposals", { estimate_id: estimateId });
        setProposal(res.data);
        // Fetch full proposal with estimate
        const fullRes = await API.get(`/proposals/${res.data.proposal_id}`);
        setProposal(fullRes.data);
        setEstimate(fullRes.data.estimate);
        setCompany(fullRes.data.company);
        navigate(`/proposal-builder/${res.data.proposal_id}`, { replace: true });
      }
      
      // Fetch terms templates
      const termsRes = await API.get("/terms-templates");
      setTermsTemplates(termsRes.data);
      
    } catch (error) {
      toast.error("Failed to load proposal");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [proposalId, estimateId, navigate]);

  useEffect(() => {
    fetchProposal();
  }, [fetchProposal]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put(`/proposals/${proposal.proposal_id}`, {
        title: proposal.title,
        client_message: proposal.client_message,
        display_default_mode: proposal.display_default_mode,
        allow_client_switch_views: proposal.allow_client_switch_views,
        financing_enabled: proposal.financing_enabled,
        financing_message: proposal.financing_message,
        terms_content: proposal.terms_content,
        signature_required: proposal.signature_required,
        payment_config: proposal.payment_config,
        client_name: proposal.client_name,
        client_email: proposal.client_email,
        client_phone: proposal.client_phone,
        project_address: proposal.project_address
      });
      toast.success("Proposal saved");
    } catch (error) {
      toast.error("Failed to save proposal");
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    try {
      await handleSave();
      const res = await API.post(`/proposals/${proposal.proposal_id}/send`, sendForm);
      toast.success(`Proposal sent via ${res.data.sent_via.join(" and ")}`);
      setSendDialogOpen(false);
      fetchProposal();
    } catch (error) {
      toast.error("Failed to send proposal");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', getFileType(file.name));
      
      const res = await API.post('/upload-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setNewAttachment({
        file_url: res.data.url,
        file_type: getFileType(file.name),
        filename: file.name,
        label: newAttachment.label || file.name.split('.')[0]
      });
      toast.success("File uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setUploadingFile(false);
    }
  };
  
  const getFileType = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    if (['pdf'].includes(ext)) return 'pdf';
    if (['doc', 'docx'].includes(ext)) return 'document';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'].includes(ext)) return 'photo';
    return 'document';
  };

  const handleAddAttachment = async () => {
    if (!newAttachment.file_url) {
      toast.error(attachmentMode === "upload" ? "Please upload a file" : "URL is required");
      return;
    }
    try {
      await API.post(`/proposals/${proposal.proposal_id}/attachments`, newAttachment);
      toast.success("Attachment added");
      setAttachmentDialogOpen(false);
      setNewAttachment({ file_url: "", file_type: "document", filename: "", label: "" });
      fetchProposal();
    } catch (error) {
      toast.error("Failed to add attachment");
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await API.delete(`/proposals/${proposal.proposal_id}/attachments/${attachmentId}`);
      toast.success("Attachment removed");
      fetchProposal();
    } catch (error) {
      toast.error("Failed to remove attachment");
    }
  };

  const calculateTotal = () => {
    if (!estimate?.sections) return 0;
    return estimate.sections.reduce((sum, sec) => 
      sum + (sec.line_items?.reduce((s, i) => {
        const unitCost = i.unit_cost !== undefined ? i.unit_cost : ((i.material_unit_cost || 0) + (i.labor_unit_cost || 0));
        const cost = (i.quantity || 0) * unitCost;
        const markup = cost * ((i.markup_percent || 0) / 100);
        return s + cost + markup;
      }, 0) || 0)
    , 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!proposal || !estimate) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">Proposal not found</p>
        <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
      </div>
    );
  }

  const grandTotal = calculateTotal();

  return (
    <div className="h-full overflow-y-auto bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800/90 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-slate-300 hover:text-white hover:bg-slate-700">
              <ArrowLeft size={18} className="mr-1" /> Back
            </Button>
            <div>
              <h1 className="font-bold text-lg text-white">Proposal Builder</h1>
              <p className="text-sm text-slate-400">{proposal.estimate_number}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${proposal.status === "draft" ? "bg-slate-600" : proposal.status === "sent" ? "bg-blue-600" : "bg-emerald-600"} text-white`}>
              {proposal.status.replace("_", " ")}
            </Badge>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(true)} className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white">
              <Eye size={16} className="mr-2" /> Preview
            </Button>
            <Button onClick={() => setSendDialogOpen(true)} className="bg-orange-500 hover:bg-orange-600">
              <Send size={16} className="mr-2" /> Preview & Send
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-6 space-y-6 pb-20">
        {/* Header Block */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="border-b border-slate-700">
            <CardTitle className="flex items-center gap-2 text-base text-white">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Building2 size={18} className="text-orange-400" />
              </div>
              Company Header
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex items-start gap-6">
              <div className="w-24 h-24 bg-slate-700 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-600">
                {company?.logo_url ? (
                  <img src={company.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                ) : (
                  <span className="text-xs text-slate-400 text-center">Logo</span>
                )}
              </div>
              <div className="flex-1 space-y-1 text-sm">
                <p className="font-semibold text-white">{company?.company_name || "Your Company"}</p>
                <p className="text-slate-400">{company?.address || "Company Address"}</p>
                <p className="text-slate-400">{company?.email}</p>
                <p className="text-slate-400">{company?.phone}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Proposal Title */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="border-b border-slate-700">
            <CardTitle className="flex items-center gap-2 text-base text-white">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <FileText size={18} className="text-orange-400" />
              </div>
              Proposal Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            <div>
              <Label className="text-slate-300">Proposal Title</Label>
              <Input
                value={proposal.title}
                onChange={(e) => setProposal({ ...proposal, title: e.target.value })}
                placeholder="Project Proposal"
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Estimate #</Label>
                <p className="font-medium text-white">{proposal.estimate_number}</p>
              </div>
              <div>
                <Label className="text-slate-400">Date</Label>
                <p className="font-medium text-white">{new Date(proposal.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Prepared For */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="border-b border-slate-700">
            <CardTitle className="flex items-center gap-2 text-base text-white">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Mail size={18} className="text-orange-400" />
              </div>
              Prepared For
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Client Name</Label>
                <Input
                  value={proposal.client_name}
                  onChange={(e) => setProposal({ ...proposal, client_name: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input
                  type="email"
                  value={proposal.client_email}
                  onChange={(e) => setProposal({ ...proposal, client_email: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Phone</Label>
                <Input
                  value={proposal.client_phone}
                  onChange={(e) => setProposal({ ...proposal, client_phone: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Project Address</Label>
                <Input
                  value={proposal.project_address}
                  onChange={(e) => setProposal({ ...proposal, project_address: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Client Message */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="border-b border-slate-700">
            <CardTitle className="flex items-center gap-2 text-base text-white">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Edit2 size={18} className="text-orange-400" />
              </div>
              Client Message
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <Textarea
              value={proposal.client_message}
              onChange={(e) => {
                setProposal({ ...proposal, client_message: e.target.value });
              }}
              rows={12}
              style={{ minHeight: '200px', resize: 'vertical' }}
              placeholder="Write a personalized message to your client..."
              className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
            />
          </CardContent>
        </Card>

        {/* Estimate Display Options */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="border-b border-slate-700">
            <CardTitle className="flex items-center gap-2 text-base text-white">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <DollarSign size={18} className="text-orange-400" />
              </div>
              Estimate Display Options
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            <div>
              <Label className="text-slate-300">Default View Mode</Label>
              <Select 
                value={proposal.display_default_mode} 
                onValueChange={(v) => setProposal({ ...proposal, display_default_mode: v })}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(VIEW_MODES).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">This is the default view shown to the client</p>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-slate-300">Allow Client to Switch Views</Label>
                <p className="text-xs text-slate-400">Client can toggle between view modes</p>
              </div>
              <Switch
                checked={proposal.allow_client_switch_views}
                onCheckedChange={(v) => setProposal({ ...proposal, allow_client_switch_views: v })}
              />
            </div>
            
            {/* Estimate Preview */}
            <div className="border-t border-slate-700 pt-4">
              <p className="text-sm font-medium mb-3 text-slate-300">Estimate Preview ({VIEW_MODES[proposal.display_default_mode] || "Lump Sum"})</p>
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-3 border border-slate-600">
                {estimate?.sections?.length > 0 ? (
                  <>
                    {/* Lump Sum View - Just show total */}
                    {proposal.display_default_mode === "lump_sum" && (
                      <div className="text-center py-4">
                        <p className="text-sm text-slate-400 mb-1">Project Total</p>
                        <p className="text-3xl font-bold text-orange-400">{fmt(grandTotal)}</p>
                      </div>
                    )}
                    
                    {/* Bulleted View - Section names only */}
                    {proposal.display_default_mode === "bulleted" && (
                      <div>
                        <ul className="list-disc list-inside space-y-1 text-sm text-slate-300 mb-4">
                          {estimate.sections.map((section, i) => (
                            <li key={i}>{section.name}</li>
                          ))}
                        </ul>
                        <div className="border-t border-slate-600 pt-3 flex justify-between font-semibold">
                          <span className="text-white">Total</span>
                          <span className="text-orange-400">{fmt(grandTotal)}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Item Names View - Section with item names */}
                    {proposal.display_default_mode === "item_names" && (
                      <div className="space-y-3">
                        {estimate.sections.map((section, i) => (
                          <div key={i}>
                            <p className="font-medium text-sm text-white">{section.name}</p>
                            <ul className="list-disc list-inside text-xs text-slate-400 ml-2">
                              {section.line_items?.slice(0, 3).map((item, j) => (
                                <li key={j}>{item.item_name || item.description}</li>
                              ))}
                              {section.line_items?.length > 3 && (
                                <li className="text-slate-500">+{section.line_items.length - 3} more items...</li>
                              )}
                            </ul>
                          </div>
                        ))}
                        <div className="border-t border-slate-600 pt-3 flex justify-between font-semibold">
                          <span className="text-white">Total</span>
                          <span className="text-orange-400">{fmt(grandTotal)}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Item Totals View - Section with item prices */}
                    {proposal.display_default_mode === "item_totals" && (
                      <div className="space-y-3">
                        {estimate.sections.map((section, i) => {
                          const sectionTotal = section.section_total || section.line_items?.reduce((sum, item) => sum + (item.line_total || 0), 0) || 0;
                          return (
                            <div key={i} className="flex justify-between text-sm">
                              <span className="text-slate-300">{section.name}</span>
                              <span className="font-medium text-white">{fmt(sectionTotal)}</span>
                            </div>
                          );
                        })}
                        <div className="border-t border-slate-600 pt-3 flex justify-between font-semibold">
                          <span className="text-white">Total</span>
                          <span className="text-orange-400">{fmt(grandTotal)}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Full Breakdown View */}
                    {proposal.display_default_mode === "full_breakdown" && (
                      <div className="space-y-4 max-h-64 overflow-y-auto">
                        {estimate.sections.map((section, i) => (
                          <div key={i}>
                            <p className="font-medium text-sm text-white border-b border-slate-600 pb-1 mb-2">{section.name}</p>
                            <div className="space-y-1">
                              {section.line_items?.map((item, j) => (
                                <div key={j} className="flex justify-between text-xs">
                                  <span className="text-slate-400">{item.item_name || item.description}</span>
                                  <span className="text-slate-300">{fmt(item.line_total || 0)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                        <div className="border-t border-slate-600 pt-3 flex justify-between font-semibold">
                          <span className="text-white">Grand Total</span>
                          <span className="text-orange-400">{fmt(grandTotal)}</span>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-slate-400 mb-1">Grand Total</p>
                    <p className="text-3xl font-bold text-orange-400">{fmt(grandTotal)}</p>
                    <p className="text-xs text-slate-500 mt-2">No line items in estimate</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financing */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="border-b border-slate-700">
            <CardTitle className="flex items-center justify-between text-base text-white">
              <span className="flex items-center gap-2">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <CreditCard size={18} className="text-orange-400" />
                </div>
                Financing
              </span>
              <Switch
                checked={proposal.financing_enabled}
                onCheckedChange={(v) => setProposal({ ...proposal, financing_enabled: v })}
              />
            </CardTitle>
          </CardHeader>
          {proposal.financing_enabled && (
            <CardContent className="pt-4">
              <Textarea
                value={proposal.financing_message}
                onChange={(e) => setProposal({ ...proposal, financing_message: e.target.value })}
                placeholder="Financing available! Ask about our payment plans..."
                rows={2}
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
              />
            </CardContent>
          )}
        </Card>

        {/* Attachments */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="border-b border-slate-700">
            <CardTitle className="flex items-center justify-between text-base text-white">
              <span className="flex items-center gap-2">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <Image size={18} className="text-orange-400" />
                </div>
                Attachments
              </span>
              <Button size="sm" variant="outline" onClick={() => setAttachmentDialogOpen(true)} className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {proposal.attachments?.length > 0 ? (
              <div className="space-y-2">
                {proposal.attachments.map((att) => (
                  <div key={att.attachment_id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                    <div className="flex items-center gap-3">
                      {att.file_type === "photo" ? <Image size={18} className="text-slate-400" /> : <File size={18} className="text-slate-400" />}
                      <div>
                        <p className="font-medium text-sm text-white">{att.label || att.filename || "Attachment"}</p>
                        <p className="text-xs text-slate-400">{att.file_type}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteAttachment(att.attachment_id)} className="text-slate-400 hover:text-red-400 hover:bg-slate-700">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">No attachments yet</p>
            )}
          </CardContent>
        </Card>

        {/* Terms & Conditions */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="border-b border-slate-700">
            <CardTitle className="flex items-center gap-2 text-base text-white">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <FileText size={18} className="text-orange-400" />
              </div>
              Terms & Conditions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div>
              <Label className="text-slate-300">Load Template</Label>
              <Select onValueChange={(v) => {
                const template = termsTemplates.find(t => t.template_id === v);
                if (template) {
                  setProposal({ ...proposal, terms_content: template.content, terms_template_id: v });
                }
              }}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  {termsTemplates.map(t => (
                    <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea
              value={proposal.terms_content}
              onChange={(e) => setProposal({ ...proposal, terms_content: e.target.value })}
              rows={8}
              placeholder="Enter terms and conditions..."
              className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
            />
          </CardContent>
        </Card>

        {/* Payment Setup */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="border-b border-slate-700">
            <CardTitle className="flex items-center justify-between text-base text-white">
              <span className="flex items-center gap-2">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <DollarSign size={18} className="text-orange-400" />
                </div>
                Payment Setup
              </span>
              <Switch
                checked={proposal.payment_config?.enabled}
                onCheckedChange={(v) => setProposal({ 
                  ...proposal, 
                  payment_config: { ...proposal.payment_config, enabled: v }
                })}
              />
            </CardTitle>
          </CardHeader>
          {proposal.payment_config?.enabled && (
            <CardContent className="space-y-5 pt-5">
              {/* Payment Methods */}
              <div>
                <Label className="text-slate-300 text-sm font-medium">Payment Methods</Label>
                <div className="flex gap-4 mt-3">
                  <label className="flex items-center gap-3 px-4 py-3 bg-slate-700/50 rounded-lg border border-slate-600 hover:border-orange-500/50 cursor-pointer transition-all">
                    <input
                      type="checkbox"
                      checked={proposal.payment_config.methods_allowed?.includes("card")}
                      onChange={(e) => {
                        const methods = e.target.checked 
                          ? [...(proposal.payment_config.methods_allowed || []), "card"]
                          : proposal.payment_config.methods_allowed?.filter(m => m !== "card") || [];
                        setProposal({
                          ...proposal,
                          payment_config: { ...proposal.payment_config, methods_allowed: methods }
                        });
                      }}
                      className="w-4 h-4 accent-orange-500"
                    />
                    <CreditCard size={16} className="text-slate-400" />
                    <span className="text-slate-200">Credit Card</span>
                  </label>
                  <label className="flex items-center gap-3 px-4 py-3 bg-slate-700/50 rounded-lg border border-slate-600 hover:border-orange-500/50 cursor-pointer transition-all">
                    <input
                      type="checkbox"
                      checked={proposal.payment_config.methods_allowed?.includes("ach")}
                      onChange={(e) => {
                        const methods = e.target.checked 
                          ? [...(proposal.payment_config.methods_allowed || []), "ach"]
                          : proposal.payment_config.methods_allowed?.filter(m => m !== "ach") || [];
                        setProposal({
                          ...proposal,
                          payment_config: { ...proposal.payment_config, methods_allowed: methods }
                        });
                      }}
                      className="w-4 h-4 accent-orange-500"
                    />
                    <Building2 size={16} className="text-slate-400" />
                    <span className="text-slate-200">ACH Bank Transfer</span>
                  </label>
                </div>
              </div>
              
              {/* Payment Option */}
              <div>
                <Label className="text-slate-300 text-sm font-medium">Payment Option</Label>
                <Select 
                  value={proposal.payment_config.payment_option || "full"}
                  onValueChange={(v) => setProposal({
                    ...proposal,
                    payment_config: { ...proposal.payment_config, payment_option: v }
                  })}
                >
                  <SelectTrigger className="mt-2 bg-slate-700/50 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full Amount</SelectItem>
                    <SelectItem value="deposit">Deposit + Balance</SelectItem>
                    <SelectItem value="draw_schedule">Draw Schedule</SelectItem>
                    <SelectItem value="custom">Custom Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Deposit Options */}
              {proposal.payment_config.payment_option === "deposit" && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-700/30 rounded-lg border border-slate-600">
                  <div>
                    <Label className="text-slate-400 text-xs">Deposit Amount ($)</Label>
                    <Input
                      type="number"
                      value={proposal.payment_config.deposit_amount || ""}
                      onChange={(e) => setProposal({
                        ...proposal,
                        payment_config: { 
                          ...proposal.payment_config, 
                          deposit_amount: parseFloat(e.target.value) || 0,
                          deposit_percent: null 
                        }
                      })}
                      placeholder="Fixed amount"
                      className="bg-slate-800 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Or Deposit %</Label>
                    <Input
                      type="number"
                      value={proposal.payment_config.deposit_percent || ""}
                      onChange={(e) => setProposal({
                        ...proposal,
                        payment_config: { 
                          ...proposal.payment_config, 
                          deposit_percent: parseFloat(e.target.value) || 0,
                          deposit_amount: null 
                        }
                      })}
                      placeholder="Percentage"
                      className="bg-slate-800 border-slate-600 text-white"
                    />
                  </div>
                </div>
              )}
              
              {/* Draw Schedule Section */}
              {proposal.payment_config.payment_option === "draw_schedule" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300 text-sm font-medium">Draw Schedule</Label>
                    <Button
                      size="sm"
                      onClick={() => {
                        const draws = proposal.payment_config.draw_schedule || [];
                        const newDraw = {
                          draw_id: `draw_${Date.now()}`,
                          name: draws.length === 0 ? "Deposit" : `Draw ${draws.length}`,
                          amount: null,
                          percent: null,
                          due_description: draws.length === 0 ? "Due upon signing" : "",
                          status: "pending",
                          order: draws.length
                        };
                        setProposal({
                          ...proposal,
                          payment_config: {
                            ...proposal.payment_config,
                            draw_schedule: [...draws, newDraw]
                          }
                        });
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      <Plus size={14} className="mr-1" /> Add Draw
                    </Button>
                  </div>
                  
                  {(!proposal.payment_config.draw_schedule || proposal.payment_config.draw_schedule.length === 0) ? (
                    <div className="text-center py-8 bg-slate-800/50 rounded-xl border-2 border-dashed border-slate-600">
                      <div className="w-12 h-12 mx-auto mb-3 bg-slate-700 rounded-full flex items-center justify-center">
                        <DollarSign className="h-6 w-6 text-slate-500" />
                      </div>
                      <p className="text-slate-400 font-medium">No draws added yet</p>
                      <p className="text-xs text-slate-500 mt-1">Add draws like Deposit, Draw 1, Draw 2, Final Payment</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {proposal.payment_config.draw_schedule.map((draw, index) => {
                        const drawAmount = draw.amount || (draw.percent ? (grandTotal * draw.percent / 100) : 0);
                        return (
                          <div 
                            key={draw.draw_id} 
                            className="p-4 bg-slate-800/80 rounded-xl border border-slate-600 hover:border-slate-500 transition-all"
                          >
                            <div className="flex items-start gap-4">
                              {/* Draw Number Badge */}
                              <div className="flex-shrink-0 w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center">
                                <span className="text-orange-400 text-sm font-bold">{index + 1}</span>
                              </div>
                              
                              <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                                <div>
                                  <Label className="text-slate-500 text-xs mb-1 block">Name</Label>
                                  <Input
                                    value={draw.name}
                                    onChange={(e) => {
                                      const draws = [...proposal.payment_config.draw_schedule];
                                      draws[index] = { ...draw, name: e.target.value };
                                      setProposal({
                                        ...proposal,
                                        payment_config: { ...proposal.payment_config, draw_schedule: draws }
                                      });
                                    }}
                                    placeholder="e.g., Deposit"
                                    className="h-9 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                                  />
                                </div>
                                <div>
                                  <Label className="text-slate-500 text-xs mb-1 block">Amount ($)</Label>
                                  <Input
                                    type="number"
                                    value={draw.amount || ""}
                                    onChange={(e) => {
                                      const draws = [...proposal.payment_config.draw_schedule];
                                      draws[index] = { 
                                        ...draw, 
                                        amount: parseFloat(e.target.value) || null,
                                        percent: null
                                      };
                                      setProposal({
                                        ...proposal,
                                        payment_config: { ...proposal.payment_config, draw_schedule: draws }
                                      });
                                    }}
                                    placeholder="Fixed $"
                                    className="h-9 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                                  />
                                </div>
                                <div>
                                  <Label className="text-slate-500 text-xs mb-1 block">Or Percent (%)</Label>
                                  <Input
                                    type="number"
                                    value={draw.percent || ""}
                                    onChange={(e) => {
                                      const draws = [...proposal.payment_config.draw_schedule];
                                      draws[index] = { 
                                        ...draw, 
                                        percent: parseFloat(e.target.value) || null,
                                        amount: null
                                      };
                                      setProposal({
                                        ...proposal,
                                        payment_config: { ...proposal.payment_config, draw_schedule: draws }
                                      });
                                    }}
                                    placeholder="%"
                                    className="h-9 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                                  />
                                </div>
                                <div>
                                  <Label className="text-slate-500 text-xs mb-1 block">Due When</Label>
                                  <Input
                                    value={draw.due_description}
                                    onChange={(e) => {
                                      const draws = [...proposal.payment_config.draw_schedule];
                                      draws[index] = { ...draw, due_description: e.target.value };
                                      setProposal({
                                        ...proposal,
                                        payment_config: { ...proposal.payment_config, draw_schedule: draws }
                                      });
                                    }}
                                    placeholder="Upon signing"
                                    className="h-9 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                                  />
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-3 pt-5">
                                <div className="text-right">
                                  <span className="text-lg font-bold text-orange-400">
                                    {fmt(drawAmount)}
                                  </span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0 text-slate-500 hover:text-red-400 hover:bg-red-500/10"
                                  onClick={() => {
                                    const draws = proposal.payment_config.draw_schedule.filter((_, i) => i !== index);
                                    setProposal({
                                      ...proposal,
                                      payment_config: { ...proposal.payment_config, draw_schedule: draws }
                                    });
                                  }}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Draw Schedule Summary */}
                      <div className="mt-4 p-4 bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl border border-slate-600">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-slate-400 text-sm">Total Scheduled</span>
                            <p className={`text-2xl font-bold ${
                              Math.abs(proposal.payment_config.draw_schedule.reduce((sum, d) => {
                                return sum + (d.amount || (d.percent ? grandTotal * d.percent / 100 : 0));
                              }, 0) - grandTotal) < 0.01 ? "text-orange-400" : "text-amber-400"
                            }`}>
                              {fmt(proposal.payment_config.draw_schedule.reduce((sum, d) => {
                                return sum + (d.amount || (d.percent ? grandTotal * d.percent / 100 : 0));
                              }, 0))}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="text-slate-400 text-sm">Project Total</span>
                            <p className="text-2xl font-bold text-white">{fmt(grandTotal)}</p>
                          </div>
                        </div>
                        {Math.abs(proposal.payment_config.draw_schedule.reduce((sum, d) => {
                          return sum + (d.amount || (d.percent ? grandTotal * d.percent / 100 : 0));
                        }, 0) - grandTotal) > 0.01 && (
                          <div className="mt-3 flex items-center gap-2 text-amber-400 text-sm">
                            <AlertTriangle size={14} />
                            <span>Draw schedule total doesn't match project total</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Custom Amount */}
              {proposal.payment_config.payment_option === "custom" && (
                <div className="p-4 bg-slate-700/30 rounded-lg border border-slate-600">
                  <Label className="text-slate-400 text-xs">Custom Amount ($)</Label>
                  <Input
                    type="number"
                    value={proposal.payment_config.custom_amount || ""}
                    onChange={(e) => setProposal({
                      ...proposal,
                      payment_config: { 
                        ...proposal.payment_config, 
                        custom_amount: parseFloat(e.target.value) || 0
                      }
                    })}
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                </div>
              )}
              
              {/* Payment Notes */}
              <div>
                <Label className="text-slate-300 text-sm font-medium">Payment Notes</Label>
                <p className="text-xs text-slate-500 mb-2">Shown to client on proposal</p>
                <Textarea
                  value={proposal.payment_config.payment_notes || ""}
                  onChange={(e) => setProposal({
                    ...proposal,
                    payment_config: { ...proposal.payment_config, payment_notes: e.target.value }
                  })}
                  placeholder="Deposit required to secure scheduling..."
                  rows={2}
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Signature */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="border-b border-slate-700">
            <CardTitle className="flex items-center justify-between text-base text-white">
              <span className="flex items-center gap-2">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <Signature size={18} className="text-orange-400" />
                </div>
                Signature Required
              </span>
              <Switch
                checked={proposal.signature_required}
                onCheckedChange={(v) => setProposal({ ...proposal, signature_required: v })}
              />
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-400">
              {proposal.signature_required 
                ? "Client must sign to approve this proposal" 
                : "No signature required - client can approve without signing"}
            </p>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-3 pb-8">
          <Button variant="outline" onClick={() => navigate(-1)} className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
            {saving ? "Saving..." : "Save Proposal"}
          </Button>
        </div>
      </div>

      {/* Send Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Proposal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={sendForm.send_email}
                  onChange={(e) => setSendForm({ ...sendForm, send_email: e.target.checked })}
                />
                Send via Email
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={sendForm.send_sms}
                  onChange={(e) => setSendForm({ ...sendForm, send_sms: e.target.checked })}
                />
                Send via SMS
              </label>
            </div>
            {sendForm.send_email && (
              <>
                <div>
                  <Label>Recipient Email</Label>
                  <Input
                    type="email"
                    value={sendForm.recipient_email}
                    onChange={(e) => setSendForm({ ...sendForm, recipient_email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email Subject</Label>
                  <Input
                    value={sendForm.email_subject}
                    onChange={(e) => setSendForm({ ...sendForm, email_subject: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email Message</Label>
                  <Textarea
                    value={sendForm.email_message}
                    onChange={(e) => setSendForm({ ...sendForm, email_message: e.target.value })}
                    rows={3}
                    placeholder="Optional message to include in email..."
                  />
                </div>
              </>
            )}
            {sendForm.send_sms && (
              <>
                <div>
                  <Label>Recipient Phone</Label>
                  <Input
                    value={sendForm.recipient_phone}
                    onChange={(e) => setSendForm({ ...sendForm, recipient_phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>SMS Message</Label>
                  <Textarea
                    value={sendForm.sms_message}
                    onChange={(e) => setSendForm({ ...sendForm, sms_message: e.target.value })}
                    rows={2}
                    placeholder="Your proposal is ready to view..."
                  />
                </div>
              </>
            )}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setSendDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSend}>
                <Send size={16} className="mr-2" /> Send Proposal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Attachment Dialog */}
      <Dialog open={attachmentDialogOpen} onOpenChange={setAttachmentDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Add Attachment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Toggle between upload and link */}
            <div className="flex gap-2 p-1 bg-slate-700 rounded-lg">
              <button
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  attachmentMode === "upload" 
                    ? "bg-orange-500 text-white" 
                    : "text-slate-400 hover:text-white"
                }`}
                onClick={() => setAttachmentMode("upload")}
              >
                <Upload size={16} className="inline mr-2" />
                Upload File
              </button>
              <button
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  attachmentMode === "link" 
                    ? "bg-orange-500 text-white" 
                    : "text-slate-400 hover:text-white"
                }`}
                onClick={() => setAttachmentMode("link")}
              >
                <FileText size={16} className="inline mr-2" />
                Add Link
              </button>
            </div>

            {attachmentMode === "upload" ? (
              <>
                {/* File Upload */}
                <div className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center hover:border-orange-500/50 transition-colors">
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.heic"
                    onChange={handleFileUpload}
                    disabled={uploadingFile}
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    {uploadingFile ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="h-10 w-10 text-orange-400 animate-spin mb-2" />
                        <p className="text-slate-400">Uploading...</p>
                      </div>
                    ) : newAttachment.file_url ? (
                      <div className="flex flex-col items-center">
                        <CheckCircle className="h-10 w-10 text-orange-400 mb-2" />
                        <p className="text-white font-medium">{newAttachment.filename}</p>
                        <p className="text-slate-400 text-sm mt-1">Click to change file</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Upload className="h-10 w-10 text-slate-500 mb-2" />
                        <p className="text-white font-medium">Click to upload</p>
                        <p className="text-slate-400 text-sm mt-1">PDF, Word, Photos</p>
                      </div>
                    )}
                  </label>
                </div>
              </>
            ) : (
              <>
                {/* Link URL */}
                <div>
                  <Label className="text-slate-300">URL</Label>
                  <Input
                    value={newAttachment.file_url}
                    onChange={(e) => setNewAttachment({ ...newAttachment, file_url: e.target.value })}
                    placeholder="https://..."
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Link Name</Label>
                  <Input
                    value={newAttachment.filename}
                    onChange={(e) => setNewAttachment({ ...newAttachment, filename: e.target.value })}
                    placeholder="Project plans, Permit document..."
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  />
                </div>
              </>
            )}

            {/* Label */}
            <div>
              <Label className="text-slate-300">Label (optional)</Label>
              <Input
                value={newAttachment.label}
                onChange={(e) => setNewAttachment({ ...newAttachment, label: e.target.value })}
                placeholder="Permit, Inspiration photo, Contract..."
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setAttachmentDialogOpen(false);
                  setNewAttachment({ file_url: "", file_type: "document", filename: "", label: "" });
                }}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddAttachment}
                disabled={!newAttachment.file_url || uploadingFile}
                className="bg-orange-500 hover:bg-orange-600"
              >
                Add Attachment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProposalBuilder;
